import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  listPostsSearch,
  listActiveServices,
  listLocationsSearch,
} from "@/lib/content.functions";
import { NAV } from "@/lib/site";

type Item = {
  title: string;
  subtitle?: string;
  kind: "Página" | "Serviço" | "Artigo" | "SEO Local";
  to: string;
  params?: Record<string, string>;
  haystack: string;
};

const STATIC_PAGES: Item[] = [
  ...NAV.map((n) => ({
    title: n.label,
    kind: "Página" as const,
    to: n.to,
    haystack: n.label.toLowerCase(),
  })),
  { title: "Método SVG", kind: "Página", to: "/metodo-svg", haystack: "metodo svg processo" },
  { title: "Sobre a SVG", kind: "Página", to: "/sobre", haystack: "sobre empresa svg multimidia" },
];

export function SiteSearch({ compact = false }: { compact?: boolean }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Lazy-load search data only after the user focuses the input.
  const [primed, setPrimed] = useState(false);
  const { data: posts = [] } = useQuery({
    queryKey: ["search-posts"],
    queryFn: () => listPostsSearch(),
    staleTime: 15 * 60_000,
    gcTime: 60 * 60_000,
    enabled: primed,
  });
  const { data: services = [] } = useQuery({
    queryKey: ["search-services"],
    queryFn: () => listActiveServices(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    enabled: primed,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ["search-locations"],
    queryFn: () => listLocationsSearch(),
    staleTime: 15 * 60_000,
    gcTime: 60 * 60_000,
    enabled: primed,
  });

  const items: Item[] = useMemo(() => {
    const a: Item[] = [...STATIC_PAGES];
    for (const s of services) {
      a.push({
        title: s.label,
        subtitle: s.description,
        kind: "Serviço",
        to: s.route_path || `/servicos/${s.slug}`,
        haystack: `${s.label} ${s.description}`.toLowerCase(),
      });
    }
    for (const p of posts) {
      a.push({
        title: p.title,
        subtitle: p.excerpt || "",
        kind: "Artigo",
        to: "/blog/$slug",
        params: { slug: p.slug },
        haystack: `${p.title} ${p.excerpt} ${p.category}`.toLowerCase(),
      });
    }
    for (const l of locations) {
      a.push({
        title: l.title,
        subtitle: l.description || `${l.service_label} • ${l.city}`,
        kind: "SEO Local",
        to: "/servicos/$slug",
        params: { slug: l.slug },
        haystack: `${l.title} ${l.city} ${l.service_label}`.toLowerCase(),
      });
    }
    return a;
  }, [posts, services, locations]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [] as Item[];
    return items.filter((i) => i.haystack.includes(term));
  }, [q, items]);

  useEffect(() => setActive(0), [q]);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  function go(item: Item) {
    setOpen(false);
    setQ("");
    navigate({ to: item.to as any, params: item.params as any });
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (results[active]) {
        e.preventDefault();
        go(results[active]);
      } else if (q.trim()) {
        e.preventDefault();
        setOpen(false);
        navigate({ to: "/blog", search: { q: q.trim() } as any });
        setQ("");
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div ref={wrapRef} className={`relative ${compact ? "w-full" : "w-full max-w-xs"}`}>
      <div className="group relative">
        <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setPrimed(true); }}
          onKeyDown={onKey}
          placeholder="Buscar páginas, serviços, artigos..."
          aria-label="Buscar no site"
          className="w-full rounded-full border border-ink/10 bg-ink/[0.04] py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground backdrop-blur transition focus:border-primary/50 focus:bg-ink/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>
      {open && q.trim().length > 0 && (
        <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-2xl border border-ink/10 bg-background/95 shadow-[0_30px_80px_-30px_oklch(0_0_0_/_0.8)] backdrop-blur-xl">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              Nenhum resultado para "{q}". Tente outra palavra-chave.
            </div>
          ) : (
            <ul role="listbox" className="max-h-[60vh] overflow-y-auto py-1">
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.title}-${i}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(r)}
                    className={`flex w-full flex-col gap-0.5 px-4 py-2.5 text-left transition ${
                      i === active ? "bg-ink/[0.06]" : "hover:bg-ink/[0.04]"
                    }`}
                  >
                    <span className="block text-sm font-medium text-foreground">{r.title}</span>
                    {r.subtitle && (
                      <span className="block line-clamp-2 text-xs text-muted-foreground">{r.subtitle}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-ink/12 px-4 py-2 text-[11px] text-muted-foreground">
            <kbd className="rounded bg-ink/5 px-1.5 py-0.5">↑↓</kbd> navegar
            <span className="mx-2">·</span>
            <kbd className="rounded bg-ink/5 px-1.5 py-0.5">Enter</kbd> abrir
            <span className="mx-2">·</span>
            <Link to="/blog" className="text-primary hover:underline" onClick={() => setOpen(false)}>
              ver todo o blog
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}